/**
 * Unified table component with variants
 *
 * @see https://ui.shadcn.com/docs/components/table
 */

import { forwardRef, useRef, useState, useCallback } from "react"
import { Download, Copy, Check, FileSpreadsheet, FileText } from "lucide-react"
import { cn } from "../../lib/utils"
import { extractTableData, generateCSV, generateMarkdown, downloadFile } from "../../lib/table-export"
import { type VariantProps, cva } from "class-variance-authority"
import { Button } from "./button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu"

// ============ TableExportActions (internal component) ============

function TableExportActions({
  tableRef,
  filename,
}: {
  tableRef: React.RefObject<HTMLTableElement | null>
  filename: string
}) {
  const [copied, setCopied] = useState(false)

  const getTableData = useCallback(() => {
    if (!tableRef.current) return null
    return extractTableData(tableRef.current)
  }, [tableRef])

  const handleDownloadCSV = useCallback(() => {
    const data = getTableData()
    if (!data) return
    const csv = generateCSV(data.headers, data.rows)
    downloadFile(csv, `${filename}.csv`, "text/csv;charset=utf-8;")
  }, [getTableData, filename])

  const handleDownloadMarkdown = useCallback(() => {
    const data = getTableData()
    if (!data) return
    const md = generateMarkdown(data.headers, data.rows)
    downloadFile(md, `${filename}.md`, "text/markdown;charset=utf-8;")
  }, [getTableData, filename])

  const handleCopyMarkdown = useCallback(async () => {
    const data = getTableData()
    if (!data) return
    const md = generateMarkdown(data.headers, data.rows)

    const showSuccess = () => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }

    try {
      await navigator.clipboard.writeText(md)
      showSuccess()
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      // Fallback for older browsers
      const textArea = document.createElement("textarea")
      textArea.value = md
      textArea.style.position = "fixed"
      textArea.style.opacity = "0"
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        document.execCommand("copy")
        showSuccess()
      } catch (fallbackErr) {
        console.error('Fallback copy method failed:', fallbackErr)
      } finally {
        document.body.removeChild(textArea)
      }
    }
  }, [getTableData])

  return (
    <div className="flex justify-end items-center gap-1 mb-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            type="button"
            aria-label="Download table"
          >
            <Download className="h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleDownloadCSV}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Download as CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDownloadMarkdown}>
            <FileText className="mr-2 h-4 w-4" />
            Download as Markdown
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        type="button"
        onClick={handleCopyMarkdown}
        aria-label="Copy table as Markdown"
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-600" />
        ) : (
          <Copy className="h-4 w-4 text-muted-foreground" />
        )}
      </Button>
    </div>
  )
}

// ============ Table Variants ============

// Table wrapper variants
const tableVariants = cva(
  "w-full overflow-auto", // base
  {
    variants: {
      variant: {
        // Default: compact, data-dense style with subtle shadow (formerly "content")
        default: "overflow-x-auto bg-white rounded-[10px] shadow-table",
        // Spacious: generous padding and rounded borders (formerly "default")
        spacious: "overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm",
        // Minimal: simple, no decoration
        minimal: "relative",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// Table element variants
const tableElementVariants = cva(
  "w-full text-sm", // base
  {
    variants: {
      variant: {
        default: "table-auto",
        spacious: "border-collapse text-gray-700",
        minimal: "caption-bottom",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// Header variants
const tableHeaderVariants = cva(
  "", // base
  {
    variants: {
      variant: {
        default: "bg-table-header-bg",
        spacious: "bg-gray-50 text-gray-500 text-xs font-semibold border-b border-gray-200",
        minimal: "[&_tr]:border-b",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// Row variants
const tableRowVariants = cva(
  "", // base
  {
    variants: {
      variant: {
        default: "border-b border-gray-200 last:border-0",
        spacious: "border-b border-gray-200 last:border-0",
        minimal: "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// Head cell variants
const tableHeadVariants = cva(
  "text-left align-middle", // base
  {
    variants: {
      variant: {
        default: "px-3 py-2.5 text-xs font-normal text-custom-text-primary uppercase tracking-table-header border-r border-gray-200 last:border-r-0",
        spacious: "px-4 py-3 font-medium border-r border-gray-300 last:border-r-0",
        minimal: "h-12 px-4 font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// Body cell variants
const tableCellVariants = cva(
  "align-middle", // base
  {
    variants: {
      variant: {
        default: "px-3 py-2.5 text-left border-r border-gray-200 last:border-r-0",
        spacious: "px-4 py-3 border-r border-gray-200 last:border-r-0",
        minimal: "p-4 [&:has([role=checkbox])]:pr-0",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// ============ Pinned Column Helpers ============

type PinnedDirection = "left" | "right"
type TableVariant = "default" | "minimal" | "spacious"

const PINNED_SHADOW = {
  left: "after:right-0 after:shadow-[2px_0_4px_rgba(0,0,0,0.06)]",
  right: "after:left-0 after:shadow-[-2px_0_4px_rgba(0,0,0,0.06)]",
} as const

const PINNED_HEAD_BG: Record<TableVariant, string> = {
  default: "bg-table-header-bg",
  spacious: "bg-gray-50",
  minimal: "bg-white",
}

function getPinnedHeadClasses(variant: string | null | undefined, pinned: PinnedDirection): string {
  const v = (variant ?? "default") as TableVariant
  const position = pinned === "left" ? "left-0" : "right-0"
  const bg = PINNED_HEAD_BG[v] ?? "bg-white"
  return `sticky ${position} z-20 ${bg} after:absolute after:top-0 after:bottom-0 after:w-px after:bg-gray-200 ${PINNED_SHADOW[pinned]}`
}

function getPinnedCellClasses(pinned: PinnedDirection): string {
  const position = pinned === "left" ? "left-0" : "right-0"
  return `sticky ${position} z-10 bg-white after:absolute after:top-0 after:bottom-0 after:w-px after:bg-gray-200 ${PINNED_SHADOW[pinned]}`
}

export interface TableProps
  extends React.HTMLAttributes<HTMLTableElement>,
    VariantProps<typeof tableVariants> {
  /** Show download/copy export actions above the table */
  enableExport?: boolean
  /** Filename for downloaded files (without extension). Default: 'table-data' */
  exportFilename?: string
}

const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, variant, children, enableExport = true, exportFilename = "table-data", ...props }, ref) => {
    const internalRef = useRef<HTMLTableElement>(null)

    const mergedRef = useCallback(
      (node: HTMLTableElement | null) => {
        internalRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref]
    )

    if (enableExport) {
      return (
        <div>
          <TableExportActions tableRef={internalRef} filename={exportFilename} />
          <div className={cn(tableVariants({ variant }))}>
            <table
              ref={mergedRef}
              className={cn(tableElementVariants({ variant }), className)}
              {...props}
            >
              {children}
            </table>
          </div>
        </div>
      )
    }

    return (
      <div className={cn(tableVariants({ variant }))}>
        <table
          ref={ref}
          className={cn(tableElementVariants({ variant }), className)}
          {...props}
        >
          {children}
        </table>
      </div>
    )
  }
)
Table.displayName = "Table"

export interface TableHeaderProps
  extends React.HTMLAttributes<HTMLTableSectionElement>,
    VariantProps<typeof tableHeaderVariants> {}

const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, variant, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn(tableHeaderVariants({ variant }), className)}
      {...props}
    />
  )
)
TableHeader.displayName = "TableHeader"

export type TableBodyProps = React.HTMLAttributes<HTMLTableSectionElement>

const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
)
TableBody.displayName = "TableBody"

const TableFooter = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

export interface TableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement>,
    VariantProps<typeof tableRowVariants> {}

const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, variant, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(tableRowVariants({ variant }), className)}
      {...props}
    />
  )
)
TableRow.displayName = "TableRow"

export interface TableHeadProps
  extends React.ThHTMLAttributes<HTMLTableCellElement>,
    VariantProps<typeof tableHeadVariants> {
  /** Pin this header cell to the left or right edge during horizontal scroll */
  pinned?: PinnedDirection
}

const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, variant, pinned, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        tableHeadVariants({ variant }),
        pinned && getPinnedHeadClasses(variant, pinned),
        className,
      )}
      {...props}
    />
  )
)
TableHead.displayName = "TableHead"

export interface TableCellProps
  extends React.TdHTMLAttributes<HTMLTableCellElement>,
    VariantProps<typeof tableCellVariants> {
  /** Pin this body cell to the left or right edge during horizontal scroll */
  pinned?: PinnedDirection
}

const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, variant, pinned, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        tableCellVariants({ variant }),
        pinned && getPinnedCellClasses(pinned),
        className,
      )}
      {...props}
    />
  )
)
TableCell.displayName = "TableCell"

const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
